/**
 * Client core
 *
 * No dependencies.
 * Does three unrelated things:
 * 1. sets up polyfills where necessary
 * 2. sets up PS's model base classes
 * 3. sets up the model and view for PS's backgrounds
 *
 * The background is mostly here so the new background can be loaded ASAP.
 *
 * @author Guangcong Luo <guancongluo@gmail.com>
 * @license AGPLv3
 */

import { Config, PS } from "./client-main";
declare const ColorThief: any;

/**********************************************************************
 * PS Models
 *********************************************************************/
// PS's model classes are defined here

const PSURL = `${document.location.protocol !== 'http:' ? 'https:' : ''}//${Config.routes.client}/`;

export class PSSubscription<T = any> {
	observable: PSModel<T> | PSStreamModel<T>;
	listener: (value: T) => void;
	constructor(observable: PSModel<T> | PSStreamModel<T>, listener: (value: T) => void) {
		this.observable = observable;
		this.listener = listener;
	}
	unsubscribe() {
		const index = this.observable.subscriptions.indexOf(this);
		if (index >= 0) this.observable.subscriptions.splice(index, 1);
	}
}

/**
 * PS Models roughly implement the Observable spec. By default,
 * PSModel notifies listeners when the model is updated. With a
 * value, PSModel can also stream data out.
 *
 * Note that unlike React's usual paradigm, PS Models are not
 * immutable.
 */
export class PSModel<T = null> {
	subscriptions: PSSubscription<T>[] = [];
	subscribe(listener: (value: T) => void) {
		const subscription = new PSSubscription<T>(this, listener);
		this.subscriptions.push(subscription);
		return subscription;
	}
	subscribeAndRun(listener: (value: T) => void, value?: T) {
		const subscription = this.subscribe(listener);
		subscription.listener(value!);
		return subscription;
	}
	update(this: PSModel): void;
	update(value: T): void;
	update(value?: T) {
		for (const subscription of this.subscriptions) {
			subscription.listener(value!);
		}
	}
}

/**
 * @see PSModel
 *
 * The main difference is that StreamModel keeps a backlog,
 * so events generated before something subscribes are not
 * lost. Nullish values are not kept in the backlog.
 */
export class PSStreamModel<T = string> {
	subscriptions: PSSubscription<T>[] = [];
	backlog: NonNullable<T>[] | null = [];
	subscribe(listener: (value: T) => void) {
		const subscription: PSSubscription<T> = new PSSubscription<T>(this, listener);
		this.subscriptions.push(subscription);
		if (this.backlog) {
			for (const update of this.backlog) {
				subscription.listener(update);
			}
			this.backlog = null;
		}
		return subscription;
	}
	subscribeAndRun(listener: (value: T) => void, value: T = null!) {
		const subscription = this.subscribe(listener);
		subscription.listener(value);
		return subscription;
	}
	update(value: T) {
		if (!this.subscriptions.length && value !== null && value !== undefined) {
			// save updates for later
			(this.backlog ||= []).push(value);
		}
		for (const subscription of this.subscriptions) {
			subscription.listener(value);
		}
	}
}

// type JSONObject = {[k: string]: JSONValue};
// type JSONArray = JSONValue[];
// type JSONValue = number | string | boolean | null | JSONObject | JSONArray;

/**********************************************************************
 * Background Model
 *********************************************************************/

/**
 * PS background model. Separate from PSPrefs because unlike prefs,
 * backgrounds can be set separately per server, instead of being
 * shared among all servers.
 *
 * Streams the current URL
 */
export const PSBackground = new class extends PSStreamModel<string | null> {
	id = '';
	curId = '';
	attrib: { url: string, title: string, artist: string } | null = null;
	changeCount = 0;
	menuColors: string[] | null = null;

	constructor() {
		super();
		try {
			let bg = localStorage.getItem('showdown_bg')?.split('\n') || [''];
			if (bg.length === 1) {
				// id
				this.load('', bg[0]);
			} else if (bg.length === 2) {
				// url, id
				this.load(bg[0], bg[1]);
			} else if (bg.length >= 7) {
				// url, id, menuColors
				this.load(bg[0], bg[1], bg.slice(2));
			}
		} catch {}
	}
	save(bgUrl: string) {
		if (this.id !== 'custom') {
			localStorage.setItem('showdown_bg', this.id);
		} else if (this.menuColors) {
			localStorage.setItem('showdown_bg', bgUrl + '\n' + this.id + '\n' + this.menuColors.join('\n'));
		} else {
			localStorage.setItem('showdown_bg', bgUrl + '\n' + this.id);
		}
	}
	set(bgUrl: string, bgid: string) {
		this.load(bgUrl, bgid);
		this.save(bgUrl);
	}

	load(bgUrl: string, bgid: string, menuColors: string[] | null = null) {
		// id
		this.id = bgid;

		// curid
		if (!bgid || bgid === 'waterfall') {
			if (location.host === 'smogtours.psim.us') {
				bgid = 'shaymin';
			} else if (location.host === Config.routes.client) {
				const bgs = ['horizon', 'ocean', 'shaymin', 'charizards'];
				bgid = bgs[Math.floor(Math.random() * bgs.length)];
				// if someone clicked the random button, try to roll a different bg than before
				if (bgid === this.curId) bgid = bgs[Math.floor(Math.random() * bgs.length)];
			} else {
				// Custom server (Eternity): leave body clear so server CSS wallpapers show
				this.curId = '';
				this.attrib = null;
				this.menuColors = null;
				this.changeCount++;
				this.update('');
				return;
			}
		}
		this.curId = bgid;

		if (bgid === 'charizards') {
			// Eternity replaces official Charizards with local Mew shiny (nginx serves it; Smogon CDN does not)
			bgUrl = '/client/img/bg-mewshiny.jpg';
		} else if (bgid === 'horizon') {
			// Eternity replaces official Horizon with local Starters wallpaper
			bgUrl = '/client/img/bg-starters.jpg';
		} else if (bgid === 'ocean') {
			bgUrl = '/client/img/bg-greninja.jpg';
		} else if (bgid === 'shaymin') {
			bgUrl = '/client/img/bg-fantasmas.jpg';
		} else if (bgid === 'lastsupper') {
			bgUrl = '/client/img/bg-lastsupper.jpg';
		} else if (bgid === 'solidblue') {
			// Eternity replaces official solid blue with local Mega Mewtwo wallpaper
			bgUrl = '/client/img/bg-megamewtwo.jpg';
		} else if (!bgUrl) {
			bgUrl = PSURL + 'fx/client-bg-' + bgid + '.jpg';
		}

		// April Fool's 2016 - Digimon theme
		// bgid = 'digimon';
		// bgUrl = PSURL + 'sprites/afd/digimonbg.jpg';

		this.changeCount++;

		// menuColors, attrib
		let attrib = null;
		switch (bgid) {
		case 'horizon':
			menuColors = [
				"114.71696331617957,20.3608643826662%",
				"64.42142371204159,25.276305710578562%",
				"64.98381070983812,27.564663680208707%",
				"128.47889415662306,21.41005258652318%",
				"68.41804111176276,39.192128721197506%",
				"58.61506103151348,67.20428158940717%",
			];
			attrib = {
				url: 'https://r4.wallpaperflare.com/wallpaper/927/247/737/pokemon-nature-pikachu-charmander-wallpaper-4664237548c040be06a0d9e164fe6dd4.jpg',
				title: 'Starters',
				artist: 'Eternity',
			};
			break;
		case 'ocean':
			menuColors = [
				"198.60508953817146,14.319835423934691%",
				"179.37101185050136,38.08899790553633%",
				"187.2991790200458,66.54055149649162%",
				"191.6264998007645,23.31532585506846%",
				"204.08635431578136,54.668321124655414%",
				"192.68280977767577,67.40434449343545%",
			];
			attrib = {
				url: 'https://r4.wallpaperflare.com/wallpaper/903/389/99/pokemon-charizard-mega-charizard-x-greninja-wallpaper-684cd28639f3b02eb69a9385afe5ea6f.jpg',
				title: 'Greninja vs Charizard',
				artist: 'Eternity',
			};
			break;
		case 'shaymin':
			menuColors = [
				"275.0006635700066,76.41019140575484%",
				"274.3159061833689,75.13954486904252%",
				"274.73183134140004,77.68070812790127%",
				"274.84215493902286,79.04957000929356%",
				"273.7662147306706,70.44125808241807%",
				"276.9532644196774,81.72087391998238%",
			];
			attrib = {
				url: 'https://r4.wallpaperflare.com/wallpaper/162/64/1018/1920x1200-px-gengar-ghastly-ghosts-haunter-pokemon-technology-vehicles-hd-art-wallpaper-2291c2d08d56be7b2a18f225e0a83972.jpg',
				title: 'Fantasmas',
				artist: 'Eternity',
			};
			break;
		case 'lastsupper':
			menuColors = [
				"29.967235561641154,55.38882418267017%",
				"34.81470043236565,42.43357468138781%",
				"32.34114262227702,44.996393630596806%",
				"25.368116935582602,43.02614668267914%",
				"38.33193619608723,36.15682457555849%",
				"24.013051266740597,49.5312739314366%",
			];
			attrib = {
				url: 'https://r4.wallpaperflare.com/wallpaper/492/398/507/pokemon-rayquaza-arceus-mewtwo-wallpaper-d8f6ed10ac0a19cfbdd00734847886c5.jpg',
				title: 'La Última Cena',
				artist: 'Eternity',
			};
			break;
		case 'charizards':
			menuColors = [
				"200.83445491251686,83.20759165389602%",
				"183.06512175613224,92.40466047323306%",
				"191.2781422472144,92.74975049096939%",
				"190.1966958067862,90.31027849795099%",
				"188.6105240166257,89.9765323345125%",
				"193.64366979759225,90.91278103929099%",
			];
			attrib = {
				url: 'https://i.ibb.co/tP8zSpVx/mewshiny.jpg',
				title: 'Mew shiny',
				artist: 'Eternity',
			};
			break;
		case 'digimon':
			menuColors = [
				"170.45454545454544,27.500000000000004%",
				"84.70588235294119,13.821138211382115%",
				"112.50000000000001,7.8431372549019605%",
				"217.82608695652175,54.761904761904766%",
				"0,1.6949152542372816%",
				"",
			];
		}
		if (!menuColors && bgUrl.startsWith('#')) {
			const r = parseInt(bgUrl.slice(1, 3), 16) / 255;
			const g = parseInt(bgUrl.slice(3, 5), 16) / 255;
			const b = parseInt(bgUrl.slice(5, 7), 16) / 255;
			const hs = this.getHueSat(r, g, b);
			menuColors = [hs, hs, hs, hs, hs, hs];
		}
		this.attrib = attrib;
		this.menuColors = menuColors;
		if (!menuColors) {
			this.extractMenuColors(bgUrl);
		}
		this.update(bgUrl);
	}
	extractMenuColors(bgUrl: string) {
		const changeCount = this.changeCount;
		// We need the image object to load it on a canvas to detect the main color.
		const img = new Image();
		img.onload = () => {
			if (changeCount !== PSBackground.changeCount) return;
			if (window.ColorThief) {
				this.extractMenuColorsFromImg(img, bgUrl);
			} else {
				PS.libsLoaded.then(() => {
					if (changeCount !== PSBackground.changeCount) return;
					this.extractMenuColorsFromImg(img, bgUrl);
				});
			}
		};
		img.src = bgUrl;
	}
	extractMenuColorsFromImg(img: HTMLImageElement, bgUrl: string) {
		// in case ColorThief throws from canvas,
		// or localStorage throws
		try {
			const colorThief = new ColorThief();
			const colors = colorThief.getPalette(img, 5);

			let menuColors = [];
			if (!colors) {
				menuColors = ['0, 0%', '0, 0%', '0, 0%', '0, 0%', '0, 0%'];
			} else {
				for (let i = 0; i < 5; i++) {
					const color = colors[i];
					const hs = PSBackground.getHueSat(color[0] / 255, color[1] / 255, color[2] / 255);
					menuColors.unshift(hs);
				}
			}
			this.menuColors = menuColors;
			this.update(null);
			PSBackground.save(bgUrl);
		} catch {}
	}
	getHueSat(r: number, g: number, b: number) {
		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		if (max === min) {
			return `0,0%`;
		}
		const l = (max + min) / 2;
		const d = max - min;
		const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		let h = 0;
		switch (max) {
		case r: h = (g - b) / d + (g < b ? 6 : 0); break;
		case g: h = (b - r) / d + 2; break;
		case b: h = (r - g) / d + 4; break;
		}
		h /= 6;
		return `${h * 360},${s * 100}%`;
	}
};

/**********************************************************************
 * Core Views
 *********************************************************************/

/** Hide server `body::before` wallpapers when the user picked a PS/custom bg. */
function setEternityServerBgHidden(hidden: boolean) {
	document.body.classList.toggle('ps-custom-bg', hidden);
	let styleEl = document.getElementById('eternity-bg-override') as HTMLStyleElement | null;
	if (!hidden) {
		styleEl?.remove();
		return;
	}
	if (!styleEl) {
		styleEl = document.createElement('style');
		styleEl.id = 'eternity-bg-override';
		document.head.appendChild(styleEl);
	}
	// Inline so it wins even if custom.css is cached without .ps-custom-bg rules
	styleEl.textContent = [
		'body.ps-custom-bg::before,',
		'.dark body.ps-custom-bg::before,',
		'body.dark.ps-custom-bg::before,',
		'html[data-theme="dark"] body.ps-custom-bg::before,',
		'body[data-theme="dark"].ps-custom-bg::before,',
		'body.ps-custom-bg::after',
		'{display:none!important;content:none!important;background:none!important;}',
	].join('');
}

PSBackground.subscribe(bgUrl => {
	if (!PSBackground.curId) {
		setEternityServerBgHidden(false);
		document.body.style.background = '';
		document.body.style.backgroundSize = '';
		const buttonStyleElem = document.getElementById('mainmenubuttoncolors');
		if (buttonStyleElem) buttonStyleElem.textContent = ``;
		return;
	}

	setEternityServerBgHidden(true);
	if (bgUrl !== null) {
		let background;
		if (bgUrl.startsWith('#')) {
			background = bgUrl;
		} else if (PSBackground.curId !== 'custom') {
			background = `#546bac url(${bgUrl}) no-repeat left center fixed`;
		} else {
			background = `#546bac url(${bgUrl}) no-repeat center center fixed`;
		}
		document.body.style.background = background;
		document.body.style.backgroundSize = 'cover';
	}

	// main menu button colors
	let cssBuf = ``;
	let n = 0;
	if (PSBackground.menuColors) {
		for (const hs of PSBackground.menuColors) {
			n++;
			cssBuf += `body .button.mainmenu${n} { background: linear-gradient(to bottom,  hsl(${hs},72%),  hsl(${hs},52%)); border-color: hsl(${hs},40%); }\n`;
			cssBuf += `body .button.mainmenu${n}:hover { background: linear-gradient(to bottom,  hsl(${hs},62%),  hsl(${hs},42%)); border-color: hsl(${hs},21%); }\n`;
			cssBuf += `body .button.mainmenu${n}:active { background: linear-gradient(to bottom,  hsl(${hs},42%),  hsl(${hs},58%)); border-color: hsl(${hs},21%); }\n`;
		}
	}
	let buttonStyleElem = document.getElementById('mainmenubuttoncolors');
	if (!buttonStyleElem) {
		if (cssBuf) {
			// Create a <style> element the correct way
			// Direct construction like `new HTMLStyleElement()` throws an error,
			// so we use document.createElement instead
			buttonStyleElem = document.createElement("style");
			buttonStyleElem.id = 'mainmenubuttoncolors';
			buttonStyleElem.textContent = cssBuf;
			document.head.appendChild(buttonStyleElem);
		}
	} else {
		buttonStyleElem.textContent = cssBuf;
	}
});
// '<a href="https://vtas.deviantart.com/art/Pokemon-Horizon-312267168" target="_blank" class="subtle">"Horizon" <small>background by Vivian Zou</small></a>';
// if (attrib) attrib = '<small style="display:block;padding-bottom:4px">' + attrib + '</small>';
